import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import './Achievements.css';

function Achievements() {
  const { currentUser } = useAuth();
  const [categories, setCategories] = useState([]); // [{key, cat}]
  const [userStats, setUserStats] = useState({}); // statsField -> string[] or number
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const configPromise = getDoc(doc(db, 'config', 'Achievements-list')).then((snap) => {
      if (!snap.exists()) return;
      const data = snap.data();
      const all = Object.entries(data)
        .map(([key, cat]) => ({ key, cat }))
        .filter(({ cat }) => cat.released !== false) // released: false のカテゴリは非表示
        .map(({ key, cat }) => ({
          key,
          cat: {
            ...cat,
            items: (cat.items || []).filter(item => item.released !== false), // アイテム単位でも除外
          },
        }))
        .sort((a, b) => (a.cat.order ?? 99) - (b.cat.order ?? 99));
      setCategories(all);
    }).catch(() => {});

    const statsPromise = currentUser
      ? getDoc(doc(db, 'userStats', currentUser.uid)).then((snap) => {
          if (snap.exists()) setUserStats(snap.data());
        }).catch(() => {})
      : Promise.resolve();

    Promise.all([configPromise, statsPromise]).finally(() => setLoading(false));
  }, [currentUser]);

  function isUnlocked(cat, item) {
    if (cat.type === 'threshold') {
      const count = userStats[cat.statsField] ?? 0;
      return count >= (item.threshold ?? 0);
    }
    const unlockedIds = userStats[cat.statsField] ?? [];
    return Array.isArray(unlockedIds) && item.id ? unlockedIds.includes(item.id) : false;
  }

  const totalItems = categories.reduce((s, { cat }) => s + cat.items.length, 0);
  const unlockedCount = categories.reduce(
    (s, { cat }) => s + cat.items.filter((item) => isUnlocked(cat, item)).length,
    0
  );

  if (loading) {
    return <div className="achievements-page"><p className="achievements-loading">読み込み中…</p></div>;
  }

  return (
    <div className="achievements-page">
      <h1 className="achievements-title">🏅 実績一覧</h1>
      <p className="achievements-subtitle">
        解除済み {unlockedCount} / {totalItems}
      </p>

      {!currentUser && (
        <div className="achievements-login-notice">
          <p>
            <Link to="/login">ログイン</Link>すると実績の解除状況が反映されます。
          </p>
        </div>
      )}

      {categories.map(({ key, cat }) => (
        <section key={key} className="achievements-section">
          <h2 className="achievements-section__title">{cat.label}</h2>
          <div className="achievements-list">
            {cat.items.map((item) => {
              const unlocked = isUnlocked(cat, item);
              return (
                <div
                  key={item.threshold ?? item.id ?? item.label}
                  className={`achievement-card ${unlocked ? 'achievement-card--unlocked' : 'achievement-card--locked'}`}
                >
                  <div className="achievement-card__icon">{item.icon}</div>
                  <div className="achievement-card__body">
                    <div className="achievement-card__label">{item.label}</div>
                    {item.desc && <div className="achievement-card__desc">{item.desc}</div>}
                  </div>
                  {cat.type === 'threshold' && item.threshold != null && (
                    <div className="achievement-card__threshold">{item.threshold}{cat.unit ?? '回'}</div>
                  )}
                  <div className="achievement-card__status">{unlocked ? '✅' : '🔒'}</div>
                </div>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}

export default Achievements;
